// src/pages/EventDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaClock, 
  FaUsers,
  FaShareAlt,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaWhatsapp,
  FaEnvelope,
  FaArrowLeft,
  FaYoutube,
  FaExternalLinkAlt
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { getEventById } from '../data/eventsdata';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  // Load the event data from eventsdata.js
  useEffect(() => {
    // Simulating API call with a slight delay for better UX
    setLoading(true);
    
    setTimeout(() => {
      const foundEvent = getEventById(eventId);
      
      if (foundEvent) {
        // Add any missing fields for consistency
        const processedEvent = {
          ...foundEvent,
          // Ensure longDescription exists
          longDescription: foundEvent.longDescription || `<p>${foundEvent.description}</p>`,
          // Ensure endDate exists
          endDate: foundEvent.endDate || new Date(new Date(foundEvent.date).setHours(new Date(foundEvent.date).getHours() + 3)).toISOString()
        };
        
        setEvent(processedEvent);
        setLoading(false);
      } else {
        setError('Event not found');
        setLoading(false);
      }
    }, 500); // Simulate loading delay
  }, [eventId]);
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Format time for display
  const formatTime = (dateString) => {
    const options = { hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleTimeString('en-US', options);
  };
  
  // Check if event is in the past
  const isEventPast = (dateString) => {
    return new Date(dateString) < new Date();
  };
  
  // Calculate days until event
  const getDaysUntilEvent = (dateString) => {
    const eventDate = new Date(dateString);
    const currentDate = new Date();
    const differenceInTime = eventDate.getTime() - currentDate.getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
    return differenceInDays;
  };
  
  // Share event functionality
  const shareEvent = (platform) => {
    const url = window.location.href;
    const title = event.title;
    const text = `Check out this event at Afrovate Tech Hub: ${title}`;
    
    let shareUrl;
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`;
        break;
      default:
        shareUrl = null;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
    
    // Close share menu
    setIsShareMenuOpen(false);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Section background="white" padding="lg" className="pt-32">
        <div className="text-center">
          <Heading level={2}>Event Not Found</Heading>
          <p className="text-gray-600 mb-6">We couldn't find the event you're looking for.</p>
          <Button to="/events" variant="primary">View All Events</Button>
        </div>
      </Section>
    );
  }
  
  // Get category name
  const getCategoryName = (categoryId) => {
    switch (categoryId) {
      case 'certification':
        return 'Certification Ceremony';
      case 'conference':
        return 'Conference';
      case 'workshop':
        return 'Workshop';
      case 'field-trip':
        return 'Field Trip';
      case 'lecture':
        return 'Public Lecture';
      case 'panel-discussion':
        return 'Panel Discussion';
      case 'project':
        return 'Project';
      case 'orientation':
        return 'Orientation';
      case 'exhibition':
        return 'Exhibition';
      case 'community-service':
        return 'Community Service';
      case 'meeting':
        return 'Meeting';
      default:
        return categoryId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };
  
  return (
    <>
      <Helmet>
        <title>{event.title} - Afrovate Tech Hub Events</title>
        <meta name="description" content={event.description} />
      </Helmet>
      
      {/* Hero Banner */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: `url(${event.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-75"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <Link to="/events" className="hover:text-secondary transition-colors">Events</Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Event Details</span>
          </div>
          
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center mb-4">
                <span className="inline-block bg-secondary text-white text-sm font-medium rounded-full px-3 py-1 mr-3">
                  {getCategoryName(event.category)}
                </span>
                
                {!isEventPast(event.date) && (
                  <span className="bg-green-500 text-white text-xs font-medium rounded-full px-3 py-1">
                    {getDaysUntilEvent(event.date) === 0
                      ? 'Today!'
                      : `${getDaysUntilEvent(event.date)} days left`}
                  </span>
                )}
                
                {isEventPast(event.date) && (
                  <span className="bg-gray-500 text-white text-xs font-medium rounded-full px-3 py-1">
                    Past Event
                  </span>
                )}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {event.title}
              </h1>
              
              <div className="flex flex-wrap gap-6 text-white mb-8">
                <div className="flex items-center">
                  <FaCalendarAlt className="mr-2 text-secondary" />
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="flex items-center">
                  <FaClock className="mr-2 text-secondary" />
                  <span>{formatTime(event.date)} - {formatTime(event.endDate)}</span>
                </div>
                <div className="flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-secondary" />
                  <span>{event.location}</span>
                </div>
                {event.attendees && (
                  <div className="flex items-center">
                    <FaUsers className="mr-2 text-secondary" />
                    <span>Capacity: {event.attendees}</span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3">
                {!isEventPast(event.date) && event.registration?.required && (
                  <Button 
                    variant="secondary" 
                    size="lg" 
                    to={`/events/register/${event.id}`}
                  >
                    Register Now
                  </Button>
                )}
                
                {event.videoUrl && (
                  <Button 
                    variant="secondary" 
                    size="lg" 
                    href={event.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FaYoutube />}
                  >
                    Watch Video
                  </Button>
                )}
                
                <div className="relative">
                  <Button 
                    variant="light" 
                    size="lg" 
                    onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                    icon={<FaShareAlt />}
                  >
                    Share Event
                  </Button>
                  
                  {/* Share Menu Popup */}
                  {isShareMenuOpen && (
                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 p-1 z-50">
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => shareEvent('facebook')}
                      >
                        <FaFacebookF className="mr-3 text-blue-600" /> Facebook
                      </button>
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => shareEvent('twitter')}
                      >
                        <FaTwitter className="mr-3 text-blue-400" /> Twitter
                      </button>
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => shareEvent('linkedin')}
                      >
                        <FaLinkedinIn className="mr-3 text-blue-700" /> LinkedIn
                      </button>
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => shareEvent('whatsapp')}
                      >
                        <FaWhatsapp className="mr-3 text-green-500" /> WhatsApp
                      </button>
                      <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => shareEvent('email')}
                      >
                        <FaEnvelope className="mr-3 text-gray-500" /> Email
                      </button>
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  onClick={() => navigate(-1)}
                  icon={<FaArrowLeft />}
                  iconPosition="left"
                >
                  Back
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Event Details */}
          <div className="lg:col-span-2">
            <Card className="mb-8">
              <Heading level={3} className="mb-4">About This Event</Heading>
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: event.longDescription }} />
              {event.theme && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <div className="font-bold text-gray-700">Event Theme</div>
                  <div className="text-primary-dark text-lg font-medium">"{event.theme}"</div>
                </div>
              )}
            </Card>
            
            {/* Event Gallery */}
            {event.gallery && event.gallery.length > 0 && (
              <Card className="mb-8">
                <Heading level={3} className="mb-4">Event Gallery</Heading>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {event.gallery.map((image, index) => (
                    <div key={index} className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <img
                        src={image}
                        alt={`${event.title} - Image ${index + 1}`}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://placehold.co/400x300/1a237e/ffffff?text=Event+Image+${index + 1}`;
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Event Video */}
            {event.videoUrl && (
              <Card className="mb-8">
                <Heading level={3} className="mb-4">Event Video</Heading>
                <div className="aspect-w-16 aspect-h-9 mb-4">
                  <iframe
                    src={event.videoUrl.replace('https://youtu.be/', 'https://www.youtube.com/embed/').replace('?si=', '?')}
                    title={event.title}
                    allowFullScreen
                    className="w-full h-full rounded-lg"
                    style={{ height: '400px' }}
                  ></iframe>
                </div>
                <div className="text-center">
                  <Button 
                    variant="outline-primary" 
                    href={event.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    icon={<FaYoutube />}
                  >
                    Watch on YouTube
                  </Button>
                </div>
              </Card>
            )}
            
            {/* Schedule/Agenda */}
            {event.schedule && event.schedule.length > 0 && (
              <Card className="mb-8">
                <Heading level={3} className="mb-4">Event Schedule</Heading>
                <div className="space-y-6">
                  {event.schedule.map((item, index) => (
                    <div key={index} className="flex">
                      <div className="w-32 flex-shrink-0 text-primary-dark font-medium">
                        {item.day || item.time}
                      </div>
                      <div className="flex-grow border-l-2 border-gray-200 pl-4 pb-6 relative">
                        <div className="absolute w-3 h-3 bg-primary rounded-full -left-[7px] top-1.5"></div>
                        <h4 className="font-bold text-gray-800">{item.title}</h4>
                        <p className="text-gray-600">{item.description}</p>
                        {item.activities && (
                          <ul className="mt-2 space-y-1 text-gray-600">
                            {item.activities.map((activity, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="text-secondary mr-2">•</span>
                                <span>{activity}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Speakers/Leaders */}
            {(event.speakers || event.leaders) && (
              <Card className="mb-8">
                <Heading level={3} className="mb-4">
                  {event.speakers ? 'Speakers & Presenters' : 'Event Leaders'}
                </Heading>
                <div className="space-y-6">
                  {(event.speakers || event.leaders || []).map((person, index) => (
                    <div key={index} className="flex flex-col md:flex-row gap-4">
                      <div className="md:w-1/4 flex-shrink-0">
                        <div className="w-32 h-32 rounded-full overflow-hidden mx-auto bg-gray-200 flex items-center justify-center">
                          {person.image ? (
                            <img
                              src={person.image}
                              alt={person.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://placehold.co/300x300/1a237e/ffffff?text=${person.name.split(' ').map(n => n[0]).join('')}`;
                              }}
                            />
                          ) : (
                            <div className="text-3xl font-bold text-white">
                              {person.name.split(' ').map(n => n[0]).join('')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="md:w-3/4">
                        <h4 className="text-xl font-bold text-primary-dark">{person.name}</h4>
                        <p className="text-secondary font-medium mb-2">{person.title}</p>
                        {person.bio && <p className="text-gray-600">{person.bio}</p>}
                        {person.linkedin && (
                          <a 
                            href={person.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-primary hover:text-primary-dark mt-2"
                          >
                            <FaLinkedinIn className="mr-1" /> LinkedIn Profile <FaExternalLinkAlt className="ml-1 text-xs" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Partners & Collaborators */}
            {event.partners && event.partners.length > 0 && (
              <Card className="mb-8">
                <Heading level={3} className="mb-4">Partners & Collaborators</Heading>
                <ul className="space-y-2">
                  {event.partners.map((partner, index) => (
                    <li key={index} className="flex items-center">
                      <span className="text-secondary mr-2">•</span>
                      <span className="text-gray-700">{partner}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
          
          {/* Right Column: Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              {/* Registration Card */}
              {!isEventPast(event.date) && event.registration?.required && (
                <Card className="mb-6" shadow="lg">
                  <div className="bg-primary-dark text-white py-4 px-6 -mx-6 -mt-6 mb-6">
                    <h3 className="font-bold text-xl">Registration</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">Registration Fee:</span>
                      <span className="font-medium">{event.registration.fee || "Free"}</span>
                    </div>
                    {event.registration.availableSeats && (
                      <div className="flex justify-between border-b border-gray-200 pb-2">
                        <span className="text-gray-600">Available Seats:</span>
                        <span className="font-medium">{event.registration.availableSeats}</span>
                      </div>
                    )}
                    {event.registration.deadline && (
                      <div className="flex justify-between border-b border-gray-200 pb-2">
                        <span className="text-gray-600">Deadline:</span>
                        <span className="font-medium">{formatDate(event.registration.deadline)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pb-2">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-green-600">Open</span>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <Button 
                      variant="secondary" 
                      size="lg" 
                      to={`/events/register/${event.id}`} 
                      className="w-full"
                    >
                      Register Now
                    </Button>
                  </div>
                </Card>
              )}
              
              {/* Target Group */}
              {event.targetGroup && (
                <Card className="mb-6" shadow="md">
                  <Heading level={4} className="mb-4">Target Audience</Heading>
                  <p className="text-gray-700">{event.targetGroup}</p>
                </Card>
              )}
              
              {/* Event Location */}
              <Card className="mb-6" shadow="md">
                <Heading level={4} className="mb-4">Event Location</Heading>
                <div className="mb-4">
                  <div className="font-medium text-gray-800 mb-1">{event.location}</div>
                  <div className="text-gray-600">{event.address || ''}</div>
                </div>
                
                {/* Map Placeholder (would be Google Maps in production) */}
                <div className="h-48 bg-gray-200 rounded-lg mb-4 flex items-center justify-center">
                  <div className="text-gray-500 text-center p-4">
                    Interactive map will be available in production
                  </div>
                </div>
                
                <Button 
                  variant="outline-primary"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location + ' Sierra Leone')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  Get Directions
                </Button>
              </Card>
              
              {/* Add to Calendar */}
              {!isEventPast(event.date) && (
                <Card className="mb-6" shadow="md">
                  <Heading level={4} className="mb-4">Add to Calendar</Heading>
                  <div className="space-y-2">
                    <Button 
                      variant="outline-primary"
                      className="w-full justify-between"
                      href={`http://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${new Date(event.date).toISOString().replace(/-|:|\.\d+/g, '')}/${new Date(event.endDate).toISOString().replace(/-|:|\.\d+/g, '')}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google Calendar
                      <FaCalendarAlt />
                    </Button>
                    
                    <Button 
                      variant="outline-primary"
                      className="w-full justify-between"
                      // This is just a placeholder - in a real app, you'd use a proper iCalendar generator
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        alert('In a production app, this would download an .ics file for Apple/Outlook Calendar');
                      }}
                    >
                      Apple / Outlook
                      <FaCalendarAlt />
                    </Button>
                  </div>
                </Card>
              )}
              
              {/* Share Event */}
              <Card shadow="md">
                <Heading level={4} className="mb-4">Share This Event</Heading>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline-primary"
                    className="flex-1"
                    onClick={() => shareEvent('facebook')}
                  >
                    <FaFacebookF />
                  </Button>
                  <Button 
                    variant="outline-primary"
                    className="flex-1"
                    onClick={() => shareEvent('twitter')}
                  >
                    <FaTwitter />
                  </Button>
                  <Button 
                    variant="outline-primary"
                    className="flex-1"
                    onClick={() => shareEvent('linkedin')}
                  >
                    <FaLinkedinIn />
                  </Button>
                  <Button 
                    variant="outline-primary"
                    className="flex-1"
                    onClick={() => shareEvent('whatsapp')}
                  >
                    <FaWhatsapp />
                  </Button>
                  <Button 
                    variant="outline-primary"
                    className="flex-1"
                    onClick={() => shareEvent('email')}
                  >
                    <FaEnvelope />
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Section>
      
      {/* Call to Action */}
      <Section background="gradient-primary" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <Heading level={2} color="white" align="center" className="mb-6">
            {!isEventPast(event.date) ? (
              <>Don't Miss This Event!</>
            ) : (
              <>Interested in Future Events?</>
            )}
          </Heading>
          <p className="text-xl text-white text-opacity-90 mb-10">
            {!isEventPast(event.date) ? (
              <>
                {event.registration?.required ? (
                  <>Register now to secure your spot. Limited seats available!</>
                ) : (
                  <>Mark your calendar and join us for this exciting event!</>
                )}
              </>
            ) : (
              <>
                This event has already taken place, but you can stay updated on our upcoming events and activities.
              </>
            )}
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            {!isEventPast(event.date) && event.registration?.required ? (
              <>
                <Button 
                  variant="secondary" 
                  size="lg" 
                  to={`/events/register/${event.id}`}
                >
                  Register Now
                </Button>
                <Button 
                  variant="light" 
                  size="lg" 
                  to="/events"
                >
                  Explore Other Events
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="secondary" 
                  size="lg" 
                  to="/events"
                >
                  View Upcoming Events
                </Button>
                <Button 
                  variant="light" 
                  size="lg" 
                  to="/contact"
                >
                  Join Our Mailing List
                </Button>
              </>
            )}
          </div>
        </div>
      </Section>
    </>
  );
};

export default EventDetailPage;